import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Calculator, 
  DollarSign, 
  TrendingUp, 
  Edit2, 
  Save, 
  X, 
  ChevronDown, 
  ChevronUp,
  Plus,
  Trash2,
  FileText
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { extractErrorMessage } from '../../utils/errorHandler';

import { BACKEND_URL_EXPORT as BACKEND_URL, API } from '../../config';

const CostCalculatorTab = ({ vehicle, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  
  // Expandable sections state
  const [expandedSection, setExpandedSection] = useState(null);
  
  // Helper function to parse custom items
  const parseCustomItems = (customItems) => {
    try {
      if (typeof customItems === 'string') {
        return JSON.parse(customItems);
      }
      return Array.isArray(customItems) ? customItems : [];
    } catch (e) {
      return [];
    }
  };
  
  // Main costs
  const [formData, setFormData] = useState({
    purchase_cost_jpy: vehicle?.purchase_cost_jpy || 0,
    vanning_cost_jpy: vehicle?.vanning_cost_jpy || 0,
    exchange_rate: vehicle?.exchange_rate || 145
  });

  // Freight breakdown
  const [freightBreakdown, setFreightBreakdown] = useState({
    m3_unit: vehicle?.freight_m3_unit?.toString() || '',
    m3_rate: vehicle?.freight_m3_rate?.toString() || '',
    exchange_rate: vehicle?.freight_exchange_rate?.toString() || '148.0827' // Freight-specific exchange rate
  });

  // Inland breakdown
  const [inlandBreakdown, setInlandBreakdown] = useState({
    auction: vehicle?.inland_auction?.toString() || '',
    transport: vehicle?.inland_transport?.toString() || '',
    inspection: vehicle?.inland_inspection?.toString() || '',
    inspection_repair_charges: vehicle?.inspection_repair_charges?.toString() || '',
    treatment: vehicle?.inland_treatment?.toString() || '',
    mileage_reversal: vehicle?.inland_mileage_reversal?.toString() || '',
    export_certificate: vehicle?.inland_export_certificate?.toString() || ''
  });

  // Domestic Shipping breakdown
  const [domesticShippingBreakdown, setDomesticShippingBreakdown] = useState({
    shipping: vehicle?.domestic_shipping_shipping?.toString() || '',
    radiation: vehicle?.domestic_shipping_radiation?.toString() || '',
    photos: vehicle?.domestic_shipping_photos?.toString() || '',
    engine_number: vehicle?.domestic_shipping_engine_number?.toString() || ''
  });

  // Others breakdown
  const [othersBreakdown, setOthersBreakdown] = useState({
    dhl_charges: vehicle?.others_dhl_charges?.toString() || '',
    parking_days: vehicle?.others_parking_days?.toString() || '',
    parking_amount_per_day: vehicle?.others_parking_amount_per_day?.toString() || '',
    custom_items: parseCustomItems(vehicle?.others_custom_items)
  });

  useEffect(() => {
    setFormData({
      purchase_cost_jpy: vehicle?.purchase_cost_jpy || 0,
      vanning_cost_jpy: vehicle?.vanning_cost_jpy || 0,
      exchange_rate: vehicle?.exchange_rate || 145
    });
    
    setFreightBreakdown({
      m3_unit: vehicle?.freight_m3_unit?.toString() || '',
      m3_rate: vehicle?.freight_m3_rate?.toString() || '',
      exchange_rate: vehicle?.freight_exchange_rate?.toString() || '148.0827'
    });
    
    setInlandBreakdown({
      auction: vehicle?.inland_auction?.toString() || '',
      transport: vehicle?.inland_transport?.toString() || '',
      inspection: vehicle?.inland_inspection?.toString() || '',
      inspection_repair_charges: vehicle?.inspection_repair_charges?.toString() || '',
      treatment: vehicle?.inland_treatment?.toString() || '',
      mileage_reversal: vehicle?.inland_mileage_reversal?.toString() || '',
      export_certificate: vehicle?.inland_export_certificate?.toString() || ''
    });

    setDomesticShippingBreakdown({
      shipping: vehicle?.domestic_shipping_shipping?.toString() || '',
      radiation: vehicle?.domestic_shipping_radiation?.toString() || '',
      photos: vehicle?.domestic_shipping_photos?.toString() || '',
      engine_number: vehicle?.domestic_shipping_engine_number?.toString() || ''
    });
    
    setOthersBreakdown({
      dhl_charges: vehicle?.others_dhl_charges?.toString() || '',
      parking_days: vehicle?.others_parking_days?.toString() || '',
      parking_amount_per_day: vehicle?.others_parking_amount_per_day?.toString() || '',
      custom_items: parseCustomItems(vehicle?.others_custom_items)
    });
  }, [vehicle]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // For exchange_rate and numeric fields, allow decimals while typing
    if (name === 'exchange_rate' || name === 'purchase_cost_jpy' || name === 'vanning_cost_jpy') {
      // Allow empty string, numbers with decimals
      if (value === '' || /^\d*\.?\d*$/.test(value)) {
        setFormData({
          ...formData,
          [name]: value
        });
      }
    } else {
      // For other fields, keep original behavior
      const parsedValue = value === '' ? 0 : (isNaN(parseFloat(value)) ? 0 : parseFloat(value));
      setFormData({
        ...formData,
        [name]: value === '' ? '' : parsedValue
      });
    }
  };

  const handleFreightChange = (field, value) => {
    // Keep the value as string for input, allow decimals
    // Only validate it's a valid number format
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setFreightBreakdown({
        ...freightBreakdown,
        [field]: value
      });
    }
  };

  const handleInlandChange = (field, value) => {
    // Keep the value as string for input, allow decimals
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setInlandBreakdown({
        ...inlandBreakdown,
        [field]: value
      });
    }
  };

  const handleDomesticShippingChange = (field, value) => {
    // Keep the value as string for input, allow decimals
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setDomesticShippingBreakdown({
        ...domesticShippingBreakdown,
        [field]: value
      });
    }
  };

  const handleOthersChange = (field, value) => {
    // Keep the value as string for input, allow decimals
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setOthersBreakdown({
        ...othersBreakdown,
        [field]: value
      });
    }
  };

  const addCustomItem = () => {
    setOthersBreakdown({
      ...othersBreakdown,
      custom_items: [
        ...othersBreakdown.custom_items,
        { name: '', cost: 0, id: Date.now() }
      ]
    });
  };

  const updateCustomItem = (id, field, value) => {
    setOthersBreakdown({
      ...othersBreakdown,
      custom_items: othersBreakdown.custom_items.map(item =>
        item.id === id ? { ...item, [field]: field === 'cost' ? (value === '' ? 0 : parseFloat(value)) : value } : item
      )
    });
  };

  const deleteCustomItem = (id) => {
    setOthersBreakdown({
      ...othersBreakdown,
      custom_items: othersBreakdown.custom_items.filter(item => item.id !== id)
    });
  };

  // Calculations
  const calculateFreightTotal = () => {
    // M3 Rate is in USD, convert to JPY using freight-specific exchange rate
    // Formula: m3_unit × m3_rate (USD) × freight_exchange_rate = JPY
    const m3Unit = parseFloat(freightBreakdown.m3_unit) || 0;
    const m3RateUSD = parseFloat(freightBreakdown.m3_rate) || 0;
    const freightExchangeRate = parseFloat(freightBreakdown.exchange_rate) || 148.0827;
    
    return Math.round(m3Unit * m3RateUSD * freightExchangeRate);
  };

  const calculateDomesticShippingTotal = () => {
    return Math.round(
      (parseFloat(domesticShippingBreakdown.shipping) || 0) +
      (parseFloat(domesticShippingBreakdown.radiation) || 0) +
      (parseFloat(domesticShippingBreakdown.photos) || 0) +
      (parseFloat(domesticShippingBreakdown.engine_number) || 0)
    );
  };

  const calculateInlandTotal = () => {
    return Math.round(
      (parseFloat(inlandBreakdown.auction) || 0) +
      (parseFloat(inlandBreakdown.transport) || 0) +
      (parseFloat(inlandBreakdown.inspection) || 0) +
      (parseFloat(inlandBreakdown.inspection_repair_charges) || 0) +
      (parseFloat(inlandBreakdown.treatment) || 0) +
      (parseFloat(inlandBreakdown.mileage_reversal) || 0) +
      (parseFloat(inlandBreakdown.export_certificate) || 0) +
      calculateDomesticShippingTotal()
    );
  };

  const calculateParkingCharges = () => {
    return Math.round((parseFloat(othersBreakdown.parking_days) || 0) * (parseFloat(othersBreakdown.parking_amount_per_day) || 0));
  };

  const calculateOthersTotal = () => {
    const baseTotal = (parseFloat(othersBreakdown.dhl_charges) || 0) + calculateParkingCharges();
    const customItems = Array.isArray(othersBreakdown.custom_items) ? othersBreakdown.custom_items : [];
    const customTotal = customItems.reduce((sum, item) => sum + (parseFloat(item.cost) || 0), 0);
    return Math.round(baseTotal + customTotal);
  };

  const calculateTotalCostJPY = () => {
    return Math.round(
      (parseFloat(formData.purchase_cost_jpy) || 0) +
      calculateInlandTotal() +  // Already includes domestic shipping
      (parseFloat(formData.vanning_cost_jpy) || 0) +
      calculateFreightTotal() +
      calculateOthersTotal()
    );
  };

  const calculateTotalCostUSD = () => {
    return calculateTotalCostJPY() / (parseFloat(formData.exchange_rate) || 145);
  };

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const handleSave = async () => {
    try {
      const updateData = {
        purchase_cost_jpy: parseFloat(formData.purchase_cost_jpy) || 0,
        inland_cost_jpy: calculateInlandTotal(),
        vanning_cost_jpy: parseFloat(formData.vanning_cost_jpy) || 0,
        freight_per_car_jpy: calculateFreightTotal(),
        exchange_rate: parseFloat(formData.exchange_rate) || 145,
        // Freight breakdown - convert strings to numbers
        freight_m3_unit: parseFloat(freightBreakdown.m3_unit) || 0,
        freight_m3_rate: parseFloat(freightBreakdown.m3_rate) || 0,
        freight_exchange_rate: parseFloat(freightBreakdown.exchange_rate) || 148.0827,
        // Inland breakdown
        inland_auction: parseFloat(inlandBreakdown.auction) || 0,
        inland_transport: parseFloat(inlandBreakdown.transport) || 0,
        inland_inspection: parseFloat(inlandBreakdown.inspection) || 0,
        inspection_repair_charges: parseFloat(inlandBreakdown.inspection_repair_charges) || 0,
        inland_treatment: parseFloat(inlandBreakdown.treatment) || 0,
        inland_mileage_reversal: parseFloat(inlandBreakdown.mileage_reversal) || 0,
        inland_export_certificate: parseFloat(inlandBreakdown.export_certificate) || 0,
        // Domestic Shipping breakdown
        domestic_shipping_total: calculateDomesticShippingTotal(),
        domestic_shipping_shipping: parseFloat(domesticShippingBreakdown.shipping) || 0,
        domestic_shipping_radiation: parseFloat(domesticShippingBreakdown.radiation) || 0,
        domestic_shipping_photos: parseFloat(domesticShippingBreakdown.photos) || 0,
        domestic_shipping_engine_number: parseFloat(domesticShippingBreakdown.engine_number) || 0,
        // Others breakdown
        others_total: calculateOthersTotal(),
        others_dhl_charges: parseFloat(othersBreakdown.dhl_charges) || 0,
        others_parking_days: parseFloat(othersBreakdown.parking_days) || 0,
        others_parking_amount_per_day: parseFloat(othersBreakdown.parking_amount_per_day) || 0,
        others_custom_items: JSON.stringify(othersBreakdown.custom_items),
        // Total vehicle cost for commission calculations
        total_vehicle_cost_jpy: calculateTotalCostJPY()
      };

      await axios.put(`${API}/vehicles/${vehicle.id}`, updateData);
      toast.success('Cost calculator updated successfully!');
      setIsEditing(false);
      setExpandedSection(null);
      onUpdate();
    } catch (error) {
      console.error('Error updating cost calculator:', error);
      const message = extractErrorMessage(error, 'Failed to update cost calculator');
      toast.error(message);
    }
  };

  const handleCancel = () => {
    setFormData({
      purchase_cost_jpy: vehicle?.purchase_cost_jpy || 0,
      vanning_cost_jpy: vehicle?.vanning_cost_jpy || 0,
      exchange_rate: vehicle?.exchange_rate || 145
    });
    
    setFreightBreakdown({
      m3_unit: vehicle?.freight_m3_unit?.toString() || '',
      m3_rate: vehicle?.freight_m3_rate?.toString() || '',
      exchange_rate: vehicle?.freight_exchange_rate?.toString() || '148.0827'
    });
    
    setInlandBreakdown({
      auction: vehicle?.inland_auction?.toString() || '',
      transport: vehicle?.inland_transport?.toString() || '',
      inspection: vehicle?.inland_inspection?.toString() || '',
      inspection_repair_charges: vehicle?.inspection_repair_charges?.toString() || '',
      treatment: vehicle?.inland_treatment?.toString() || '',
      mileage_reversal: vehicle?.inland_mileage_reversal?.toString() || '',
      export_certificate: vehicle?.inland_export_certificate?.toString() || ''
    });

    setDomesticShippingBreakdown({
      shipping: vehicle?.domestic_shipping_shipping?.toString() || '',
      radiation: vehicle?.domestic_shipping_radiation?.toString() || '',
      photos: vehicle?.domestic_shipping_photos?.toString() || '',
      engine_number: vehicle?.domestic_shipping_engine_number?.toString() || ''
    });
    
    setOthersBreakdown({
      dhl_charges: vehicle?.others_dhl_charges?.toString() || '',
      parking_days: vehicle?.others_parking_days?.toString() || '',
      parking_amount_per_day: vehicle?.others_parking_amount_per_day?.toString() || '',
      custom_items: parseCustomItems(vehicle?.others_custom_items)
    });
    
    setIsEditing(false);
    setExpandedSection(null);
  };


  const formatCurrency = (value, currency = 'JPY') => {
    const numValue = parseFloat(value) || 0;
    if (currency === 'JPY') {
      return `¥${numValue.toLocaleString('ja-JP', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
    } else {
      return `$${numValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
  };

  return (
    <div className="space-y-6">
      {/* Edit/Save buttons */}
      <div className="flex justify-end space-x-2">
        {isEditing ? (
          <>
            <Button onClick={handleCancel} variant="outline" size="sm">
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
            <Button onClick={handleSave} size="sm">
              <Save className="h-4 w-4 mr-2" />
              Save Changes
            </Button>
          </>
        ) : (
          <Button onClick={() => setIsEditing(true)} size="sm">
            <Edit2 className="h-4 w-4 mr-2" />
            Edit Costs
          </Button>
        )}
      </div>

      {/* Cost Input Fields */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Calculator className="h-5 w-5" />
            <span>Cost Breakdown</span>
          </CardTitle>
          <CardDescription>Enter vehicle costs in JPY</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Purchase Cost */}
          <div>
            <Label htmlFor="purchase_cost_jpy">Purchase Cost (JPY)</Label>
            {isEditing ? (
              <Input
                id="purchase_cost_jpy"
                name="purchase_cost_jpy"
                type="text"
                inputMode="numeric"
                value={formData.purchase_cost_jpy}
                onChange={handleInputChange}
                placeholder="0"
                className="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
            ) : (
              <p className="text-lg font-medium">{formatCurrency(formData.purchase_cost_jpy)}</p>
            )}
          </div>

          {/* Inland Cost with Collapsible Breakdown */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label>Inland Cost (JPY)</Label>
              {isEditing && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleSection('inland');
                  }}
                  className="h-8"
                >
                  {expandedSection === 'inland' ? (
                    <ChevronUp className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                </Button>
              )}
            </div>
            
            {isEditing ? (
              <div
                className="cursor-pointer bg-slate-50 p-3 rounded-md border hover:bg-slate-100 transition-colors"
                onClick={() => toggleSection('inland')}
              >
                <p className="text-lg font-medium text-blue-600">
                  {formatCurrency(calculateInlandTotal())}
                </p>
              </div>
            ) : (
              <p className="text-lg font-medium">{formatCurrency(calculateInlandTotal())}</p>
            )}

            {/* Breakdown Summary - Always Visible */}
            <div className="mt-2 text-xs text-gray-600">
              Auction {formatCurrency(inlandBreakdown.auction)} + Transport {formatCurrency(inlandBreakdown.transport)} + Inspection {formatCurrency(inlandBreakdown.inspection)} + Treatment {formatCurrency(inlandBreakdown.treatment)} + Mileage Reversal {formatCurrency(inlandBreakdown.mileage_reversal)} + Export Certificate {formatCurrency(inlandBreakdown.export_certificate)} + Domestic Shipping: Shipping {formatCurrency(domesticShippingBreakdown.shipping)} + Radiation {formatCurrency(domesticShippingBreakdown.radiation)} + Photos {formatCurrency(domesticShippingBreakdown.photos)} + Engine Number {formatCurrency(domesticShippingBreakdown.engine_number)}
            </div>

            {/* Inland Breakdown */}
            {isEditing && expandedSection === 'inland' && (
              <div className="mt-3 p-4 bg-blue-50 rounded-md border border-blue-200 space-y-3">
                <div>
                  <Label htmlFor="inland_auction" className="text-sm">Auction</Label>
                  <Input
                    id="inland_auction"
                    type="text"
                    inputMode="numeric"
                    value={inlandBreakdown.auction}
                    onChange={(e) => handleInlandChange('auction', e.target.value)}
                    placeholder="0"
                    className="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  />
                </div>
                <div>
                  <Label htmlFor="inland_transport" className="text-sm">Transport</Label>
                  <Input
                    id="inland_transport"
                    type="text"
                    inputMode="numeric"
                    value={inlandBreakdown.transport}
                    onChange={(e) => handleInlandChange('transport', e.target.value)}
                    placeholder="0"
                    className="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  />
                </div>
                <div>
                  <Label htmlFor="inland_inspection" className="text-sm">Inspection</Label>
                  <Input
                    id="inland_inspection"
                    type="text"
                    inputMode="numeric"
                    value={inlandBreakdown.inspection}
                    onChange={(e) => handleInlandChange('inspection', e.target.value)}
                    placeholder="0"
                    className="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  />
                </div>
                <div>
                  <Label htmlFor="inspection_repair_charges" className="text-sm">Inspection Repair Charges</Label>
                  <Input
                    id="inspection_repair_charges"
                    type="text"
                    inputMode="numeric"
                    value={inlandBreakdown.inspection_repair_charges}
                    onChange={(e) => handleInlandChange('inspection_repair_charges', e.target.value)}
                    placeholder="0"
                    className="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  />
                </div>
                <div>
                  <Label htmlFor="inland_treatment" className="text-sm">Treatment</Label>
                  <Input
                    id="inland_treatment"
                    type="text"
                    inputMode="numeric"
                    value={inlandBreakdown.treatment}
                    onChange={(e) => handleInlandChange('treatment', e.target.value)}
                    placeholder="0"
                    className="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  />
                </div>
                <div>
                  <Label htmlFor="inland_mileage_reversal" className="text-sm">Mileage Reversal Charges (if any)</Label>
                  <Input
                    id="inland_mileage_reversal"
                    type="text"
                    inputMode="numeric"
                    value={inlandBreakdown.mileage_reversal}
                    onChange={(e) => handleInlandChange('mileage_reversal', e.target.value)}
                    placeholder="0"
                    className="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  />
                </div>
                <div>
                  <Label htmlFor="inland_export_certificate" className="text-sm">Export Certificate</Label>
                  <Input
                    id="inland_export_certificate"
                    type="text"
                    inputMode="numeric"
                    value={inlandBreakdown.export_certificate}
                    onChange={(e) => handleInlandChange('export_certificate', e.target.value)}
                    placeholder="0"
                    className="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  />
                </div>
                
                {/* Domestic Shipping Fields within Inland */}
                <div className="pt-3 mt-3 border-t border-blue-300">
                  <h4 className="text-sm font-medium text-blue-900 mb-3">Domestic Shipping</h4>
                  
                  <div className="space-y-3">
                    <div>
                      <Label htmlFor="domestic_shipping" className="text-sm">Shipping</Label>
                      <Input
                        id="domestic_shipping"
                        type="text"
                        inputMode="numeric"
                        value={domesticShippingBreakdown.shipping}
                        onChange={(e) => handleDomesticShippingChange('shipping', e.target.value)}
                        placeholder="0"
                        className="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      />
                    </div>
                    <div>
                      <Label htmlFor="domestic_radiation" className="text-sm">Radiation</Label>
                      <Input
                        id="domestic_radiation"
                        type="text"
                        inputMode="numeric"
                        value={domesticShippingBreakdown.radiation}
                        onChange={(e) => handleDomesticShippingChange('radiation', e.target.value)}
                        placeholder="0"
                        className="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      />
                    </div>
                    <div>
                      <Label htmlFor="domestic_photos" className="text-sm">Photos</Label>
                      <Input
                        id="domestic_photos"
                        type="text"
                        inputMode="numeric"
                        value={domesticShippingBreakdown.photos}
                        onChange={(e) => handleDomesticShippingChange('photos', e.target.value)}
                        placeholder="0"
                        className="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      />
                    </div>
                    <div>
                      <Label htmlFor="domestic_engine_number" className="text-sm">Engine Number</Label>
                      <Input
                        id="domestic_engine_number"
                        type="text"
                        inputMode="numeric"
                        value={domesticShippingBreakdown.engine_number}
                        onChange={(e) => handleDomesticShippingChange('engine_number', e.target.value)}
                        placeholder="0"
                        className="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      />
                    </div>
                  </div>
                </div>
                
                <div className="pt-2 border-t border-blue-300">
                  <p className="text-sm font-medium text-blue-900">
                    Total: {formatCurrency(calculateInlandTotal())}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Vanning Cost */}
          <div>
            <Label htmlFor="vanning_cost_jpy">Vanning Cost (JPY)</Label>
            {isEditing ? (
              <Input
                id="vanning_cost_jpy"
                name="vanning_cost_jpy"
                type="text"
                inputMode="numeric"
                value={formData.vanning_cost_jpy}
                onChange={handleInputChange}
                placeholder="0"
                className="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
            ) : (
              <p className="text-lg font-medium">{formatCurrency(formData.vanning_cost_jpy)}</p>
            )}
          </div>

          {/* Freight Cost with Collapsible Breakdown */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label>Freight Cost (JPY)</Label>
              {isEditing && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleSection('freight');
                  }}
                  className="h-8"
                >
                  {expandedSection === 'freight' ? (
                    <ChevronUp className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                </Button>
              )}
            </div>
            
            {isEditing ? (
              <div
                className="cursor-pointer bg-slate-50 p-3 rounded-md border hover:bg-slate-100 transition-colors"
                onClick={() => toggleSection('freight')}
              >
                <p className="text-lg font-medium text-green-600">
                  {formatCurrency(calculateFreightTotal())}
                </p>
              </div>
            ) : (
              <p className="text-lg font-medium">{formatCurrency(calculateFreightTotal())}</p>
            )}

            {/* Breakdown Summary - Always Visible */}
            <div className="mt-2 text-xs text-gray-600">
              Freight = Car Dimension m3 × M3 Rate (USD) × Exchange Rate | Total: {formatCurrency(calculateFreightTotal())}
            </div>

            {/* Freight Breakdown */}
            {isEditing && expandedSection === 'freight' && (
              <div className="mt-3 p-4 bg-green-50 rounded-md border border-green-200 space-y-3">
                <div>
                  <Label htmlFor="freight_m3_unit" className="text-sm">Car Dimension m3</Label>
                  <Input
                    id="freight_m3_unit"
                    type="text"
                    inputMode="decimal"
                    value={freightBreakdown.m3_unit}
                    onChange={(e) => handleFreightChange('m3_unit', e.target.value)}
                    placeholder="0"
                    step="0.01"
                    className="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  />
                </div>
                <div>
                  <Label htmlFor="freight_m3_rate" className="text-sm">M3 Rate (USD)</Label>
                  <Input
                    id="freight_m3_rate"
                    type="text"
                    inputMode="numeric"
                    value={freightBreakdown.m3_rate}
                    onChange={(e) => handleFreightChange('m3_rate', e.target.value)}
                    placeholder="0"
                    className="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  />
                </div>
                <div>
                  <Label htmlFor="freight_exchange_rate" className="text-sm">Exchange Rate (Freight)</Label>
                  <Input
                    id="freight_exchange_rate"
                    type="text"
                    inputMode="decimal"
                    value={freightBreakdown.exchange_rate}
                    onChange={(e) => handleFreightChange('exchange_rate', e.target.value)}
                    placeholder="148.0827"
                    className="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  />
                </div>
                <div className="pt-2 border-t border-green-300">
                  <p className="text-sm font-medium text-green-900">
                    Freight = {freightBreakdown.m3_unit || 0} m3 × ${freightBreakdown.m3_rate || 0} × ¥{freightBreakdown.exchange_rate || 148.0827}
                  </p>
                  <p className="text-sm font-medium text-green-900">
                    Total: {formatCurrency(calculateFreightTotal())}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Other(s) Cost with Collapsible Breakdown */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label>Other(s) Cost (JPY)</Label>
              {isEditing && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleSection('others');
                  }}
                  className="h-8"
                >
                  {expandedSection === 'others' ? (
                    <ChevronUp className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                </Button>
              )}
            </div>
            
            {isEditing ? (
              <div
                className="cursor-pointer bg-slate-50 p-3 rounded-md border hover:bg-slate-100 transition-colors"
                onClick={() => toggleSection('others')}
              >
                <p className="text-lg font-medium text-purple-600">
                  {formatCurrency(calculateOthersTotal())}
                </p>
              </div>
            ) : (
              <p className="text-lg font-medium">{formatCurrency(calculateOthersTotal())}</p>
            )}

            {/* Breakdown Summary - Always Visible */}
            <div className="mt-2 text-xs text-gray-600">
              DHL Charges {formatCurrency(othersBreakdown.dhl_charges)} + Parking Charges {formatCurrency(calculateParkingCharges())} + Custom Items {formatCurrency(othersBreakdown.custom_items.reduce((sum, item) => sum + (parseFloat(item.cost) || 0), 0))}
            </div>

            {/* Others Breakdown */}
            {isEditing && expandedSection === 'others' && (
              <div className="mt-3 p-4 bg-purple-50 rounded-md border border-purple-200 space-y-3">
                <div>
                  <Label htmlFor="others_dhl_charges" className="text-sm">DHL Document Charges</Label>
                  <Input
                    id="others_dhl_charges"
                    type="text"
                    inputMode="numeric"
                    value={othersBreakdown.dhl_charges}
                    onChange={(e) => handleOthersChange('dhl_charges', e.target.value)}
                    placeholder="0"
                    className="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  />
                </div>
                {/* Parking Charges with Formula */}
                <div className="border border-purple-300 p-3 rounded bg-purple-100">
                  <Label className="text-sm font-medium mb-2">Parking Charges</Label>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <div>
                      <Label htmlFor="others_parking_days" className="text-xs">Days</Label>
                      <Input
                        id="others_parking_days"
                        type="text"
                        inputMode="numeric"
                        value={othersBreakdown.parking_days}
                        onChange={(e) => handleOthersChange('parking_days', e.target.value)}
                        placeholder="12"
                        className="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      />
                    </div>
                    <div>
                      <Label htmlFor="others_parking_amount_per_day" className="text-xs">Amount/Day</Label>
                      <Input
                        id="others_parking_amount_per_day"
                        type="text"
                        inputMode="numeric"
                        value={othersBreakdown.parking_amount_per_day}
                        onChange={(e) => handleOthersChange('parking_amount_per_day', e.target.value)}
                        placeholder="500"
                        className="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      />
                    </div>
                  </div>
                  <div className="mt-2 text-xs text-purple-900 font-medium">
                    {othersBreakdown.parking_days} days × {formatCurrency(othersBreakdown.parking_amount_per_day)}/day = {formatCurrency(calculateParkingCharges())}
                  </div>
                </div>

                {/* Custom Items */}
                {othersBreakdown.custom_items.map((item) => (
                  <div key={item.id} className="flex space-x-2 items-end">
                    <div className="flex-1">
                      <Label className="text-sm">Item Name</Label>
                      <Input
                        type="text"
                        value={item.name}
                        onChange={(e) => updateCustomItem(item.id, 'name', e.target.value)}
                        placeholder="Enter item name"
                      />
                    </div>
                    <div className="flex-1">
                      <Label className="text-sm">Cost</Label>
                      <Input
                        type="text"
                        inputMode="numeric"
                        value={item.cost}
                        onChange={(e) => updateCustomItem(item.id, 'cost', e.target.value)}
                        placeholder="0"
                        className="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      />
                    </div>
                    <Button
                      type="button"
                      variant="destructive"
                      size="icon"
                      onClick={() => deleteCustomItem(item.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}

                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addCustomItem}
                  className="w-full"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Custom Item
                </Button>

                <div className="pt-2 border-t border-purple-300">
                  <p className="text-sm font-medium text-purple-900">
                    Total: {formatCurrency(calculateOthersTotal())}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Exchange Rate */}
          <div>
            <Label htmlFor="exchange_rate">Exchange Rate (JPY to USD)</Label>
            {isEditing ? (
              <Input
                id="exchange_rate"
                name="exchange_rate"
                type="text"
                inputMode="decimal"
                value={formData.exchange_rate}
                onChange={handleInputChange}
                placeholder="145"
                className="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
            ) : (
              <p className="text-lg font-medium">{formData.exchange_rate}</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Total Cost Summary */}
      <Card className="border-2 border-blue-200">
        <CardHeader className="bg-blue-50">
          <CardTitle className="flex items-center space-x-2">
            <TrendingUp className="h-5 w-5 text-blue-600" />
            <span>Total Cost Summary</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-lg font-medium text-slate-600">Total Cost (JPY):</span>
              <span className="text-2xl font-bold text-blue-600">
                {formatCurrency(calculateTotalCostJPY())}
              </span>
            </div>
            <div className="flex justify-between items-center pt-4 border-t">
              <span className="text-lg font-medium text-slate-600">Total Cost (USD):</span>
              <span className="text-2xl font-bold text-green-600">
                {formatCurrency(calculateTotalCostUSD(), 'USD')}
              </span>
            </div>
            <div className="text-sm text-slate-500 pt-2">
              <p>Exchange Rate: 1 USD = {formData.exchange_rate} JPY</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CostCalculatorTab;
